import React, { FC } from 'react';

import { GenInternalLink } from '@/components/generics';

import {
  AddressIcon,
  CreditCardIcon,
  DeliveredIcon,
  PackageIcon,
  ScheduledIcon,
  GiftIcon,
} from '@/components/ui/Icons';

import { DisplayDateFormat } from '@/utils/dates/constants';
import { formatDateForDisplay } from '@/utils/dates/dateFormatters';

import { CTA_NAMES } from '@/utils/rudderstack';

import {
  buildQueryString,
  constructLegacyCustomerPortalLink,
  SUBSCRIPTION_SOURCE_UUID_KEY,
} from '@/utils/shopSubdomain';

import { TypePlayKitPageTranslations } from '@/utils/translations/types/TypePlayKitPageTranslations';

import { InfoRow } from '../../../_components/InfoRow';

import { trackClickEvent, TypeScheduledItemGroup } from '../../utils';

interface OrderInsightsProps {
  t: TypePlayKitPageTranslations;
  group: TypeScheduledItemGroup;
  locale: string;
  showOrderNumber?: boolean;
  showOrderDate?: boolean;
  showShippingAddress?: boolean;
  showEditShippingAddress?: boolean;
  showNextPaymentDate?: boolean;
  showPrepaidDate?: boolean;
  showGiftActivationDate?: boolean;
  showPauseOrSkip?: boolean;
}

const OrderInsights: FC<OrderInsightsProps> = ({
  t,
  group,
  locale,
  showOrderDate = false,
  showOrderNumber = false,
  showShippingAddress = false,
  showEditShippingAddress = false,
  showNextPaymentDate = false,
  showPrepaidDate = false,
  showGiftActivationDate = false,
  showPauseOrSkip = false,
}) => {
  const { order, primaryPaymentMethodLocalizedLabel, subscriptionUuid } = group;

  const OrderNumber = () => (
    <InfoRow icon={PackageIcon} label={t.orderNumber}>
      <GenInternalLink
        href={`${order?.status_url}?language=${locale}`}
        cta={order?.playgenius_order_id || ''}
      />
    </InfoRow>
  );

  const OrderDate = () => (
    <InfoRow
      icon={DeliveredIcon}
      label={t.orderDate}
      value={formatDateForDisplay(
        order?.created_at || '',
        locale,
        DisplayDateFormat.ShortMonthAndDay,
      )}
    />
  );

  const GiftActivationDate = () => (
    <InfoRow
      icon={GiftIcon}
      label={t.giftActivationDate}
      value={formatDateForDisplay(
        order?.created_at || '',
        locale,
        DisplayDateFormat.ShortMonthAndDay,
      )}
    />
  );

  const queryParameters = [
    {
      key: SUBSCRIPTION_SOURCE_UUID_KEY,
      value: subscriptionUuid,
    },
  ];

  const queryString = buildQueryString(queryParameters);

  const editShipAddressLink = constructLegacyCustomerPortalLink(
    locale,
    [
      group.userUuid,
      'account-settings',
      'edit-shipping-address',
      order?.shipping_address.original_uuid || '',
    ],
    queryString,
  );

  const managePaymentMethodLink = constructLegacyCustomerPortalLink(
    locale,
    [group.userUuid, 'account-settings', 'payment-method'],
    queryString,
  );

  const OrderShippingAddress = () => (
    <InfoRow
      icon={AddressIcon}
      label={t.shippingAddress}
      value={order?.shipping_address.line1}
      shouldHideOverflow
    >
      {showEditShippingAddress && (
        <GenInternalLink
          href={editShipAddressLink}
          cta={t.edit}
          onClick={() => {
            trackClickEvent(CTA_NAMES.EDIT_SHIPPING_ADDRESS);
          }}
        />
      )}
    </InfoRow>
  );

  const NextPaymentDate = () => {
    const targetScheduledItem = group.items[0];

    const scheduledDateYear = new Date(
      targetScheduledItem.scheduled_date,
    ).getFullYear();

    const currentYear = new Date().getFullYear();

    const isDateWithinCurrentYear = scheduledDateYear === currentYear;

    const paymentDate = targetScheduledItem.scheduled_date
      ? formatDateForDisplay(
          targetScheduledItem.scheduled_date,
          locale,
          isDateWithinCurrentYear
            ? DisplayDateFormat.ShortMonthAndDay
            : DisplayDateFormat.ShortMonthDayYear,
        )
      : undefined;

    return (
      <InfoRow
        icon={ScheduledIcon}
        label={t.nextPaymentDate}
        value={paymentDate}
      />
    );
  };

  const PaymentMethod = () => (
    <InfoRow
      icon={CreditCardIcon}
      label={t.paymentMethodLabel}
      value={primaryPaymentMethodLocalizedLabel}
      shouldLineBreakValueDisplayInMobile
    >
      <GenInternalLink
        href={managePaymentMethodLink}
        cta={t.manage}
        onClick={() => {
          trackClickEvent(CTA_NAMES.MANAGE_PAYMENT_METHOD);
        }}
      />
    </InfoRow>
  );

  const PrepaidDate = () => (
    <InfoRow
      icon={ScheduledIcon}
      label={t.prepaidDate}
      value={formatDateForDisplay(
        order?.created_at || '',
        locale,
        DisplayDateFormat.ShortMonthAndDay,
      )}
    />
  );

  return (
    <div className="flex flex-col gap-2 pb-6">
      {showPrepaidDate && <PrepaidDate />}

      {showNextPaymentDate && <NextPaymentDate />}

      {primaryPaymentMethodLocalizedLabel && <PaymentMethod />}

      {showOrderDate && <OrderDate />}

      {showGiftActivationDate && <GiftActivationDate />}

      {showShippingAddress && <OrderShippingAddress />}

      {showOrderNumber && <OrderNumber />}

      {showPauseOrSkip && (
        <GenInternalLink
          href={editShipAddressLink}
          cta={t.edit}
          onClick={() => {
            trackClickEvent(CTA_NAMES.EDIT_SHIPPING_ADDRESS);
          }}
        />
      )}
    </div>
  );
};

export default OrderInsights;
